import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { CalendarDays, CheckCircle2, Search, ShieldAlert, Store } from 'lucide-react';
import ExportButton from '../../components/common/ExportButton';
import FilterSelect from '../../components/common/FilterSelect';
import { inventoryAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';
import { downloadExcelWorkbook } from '../../utils/exportExcel';

const formatLocalDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const COUNT_STATUS_OPTIONS = [
  { value: 'all', label: 'All Products' },
  { value: 'counted', label: 'Counted' },
  { value: 'uncounted', label: 'Uncounted' },
];

const buildDraftStorageKey = (storeId, dateKey) => (
  `fullInventoryDraft::${String(storeId || 'unknown')}::${String(dateKey || '')}`
);

const readDraftRows = (storageKey) => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (_) {
    return {};
  }
};

const writeDraftRows = (storageKey, rows = []) => {
  try {
    const payload = rows.reduce((acc, row) => {
      acc[row.id] = {
        shopCount: Number(row.shopCount || 0),
        warehouseCount: Number(row.warehouseCount || 0),
      };
      return acc;
    }, {});
    window.localStorage.setItem(storageKey, JSON.stringify(payload));
  } catch (_) {
    // Ignore storage errors to avoid blocking task entry.
  }
};

const FullInventoryCount = () => {
  const queryClient = useQueryClient();
  const { hasFeatureAccess } = useAuthStore();
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const canViewPage = hasFeatureAccess('inventory_count.task');
  const selectedStoreId = selectedStore?.storeInfoId || selectedStore?.store_info_id || selectedStore?.legacyStoreId || selectedStore?.legacy_store_id || '';
  const selectedStoreName = selectedStore?.name || selectedStore?.storeName || selectedStore?.store_code || selectedStore?.storeCode || 'Selected Store';
  const todayKey = formatLocalDateKey();
  const [selectedDate, setSelectedDate] = useState(todayKey);
  const [searchInput, setSearchInput] = useState('');
  const [countStatus, setCountStatus] = useState('all');
  const [rows, setRows] = useState([]);
  const [dirtyRowIds, setDirtyRowIds] = useState(() => new Set());
  const searchInputRef = useRef(null);

  const { data, isLoading, isError, error } = useQuery(
    ['fullInventoryTask', selectedStoreId, selectedDate],
    () => inventoryAPI.getFullCountTask({
      date: selectedDate,
      storeInfoId: selectedStoreId,
    }),
    {
      enabled: canViewPage && Boolean(selectedStoreId),
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );

  const meta = data?.data?.meta || {};
  const effectiveStoreId = meta.storeId || selectedStoreId || '';
  const draftStorageKey = useMemo(
    () => buildDraftStorageKey(effectiveStoreId, selectedDate),
    [effectiveStoreId, selectedDate]
  );
  const schedule = meta.schedule || null;
  const isTaskCompleted = Boolean(meta.completed);
  const completedAt = meta.completedAt || null;
  const completedBy = meta.completedBy || '';
  const taskLoadErrorMessage = error?.response?.data?.message || 'Failed to load full inventory schedule for this date.';
  const isScheduleResolved = Boolean(data?.data?.meta) || isError;
  const isScheduled = !isError && Boolean(meta.scheduled || schedule);
  const isCheckingSchedule = isLoading && !isScheduleResolved;

  useEffect(() => {
    if (!canViewPage) return;
    searchInputRef.current?.focus();
  }, [canViewPage]);

  useEffect(() => {
    const draftByRowId = readDraftRows(draftStorageKey);
    setRows((data?.data?.data || []).map((row) => {
      const draft = draftByRowId[row.id];
      const shopCount = draft ? Number(draft.shopCount || 0) : Number(row.shopCount || 0);
      const warehouseCount = draft ? Number(draft.warehouseCount || 0) : Number(row.warehouseCount || 0);
      return {
        ...row,
        shopCount,
        warehouseCount,
        totalQuantity: shopCount + warehouseCount,
        counted: Boolean(row.counted) || !!draft,
      };
    }));
    setDirtyRowIds(new Set());
  }, [data, draftStorageKey]);

  useEffect(() => {
    if (!rows.length) return;
    writeDraftRows(draftStorageKey, rows);
  }, [draftStorageKey, rows]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchInput.trim().toLowerCase();

    return rows.filter((row) => {
      if (countStatus === 'counted' && !row.counted) return false;
      if (countStatus === 'uncounted' && row.counted) return false;

      if (!normalizedSearch) return true;

      return [
        row.storeName,
        row.sellerName,
        row.sku,
        row.barcode,
        row.productName,
        row.categoryName,
        row.parentCategoryName,
      ].some((value) => String(value || '').toLowerCase().includes(normalizedSearch));
    });
  }, [countStatus, rows, searchInput]);

  const saveMutation = useMutation(
    (payload) => inventoryAPI.saveFullCountTask(payload),
    {
      onSuccess: async () => {
        toast.success('Full inventory count saved.');
        window.localStorage.removeItem(draftStorageKey);
        setDirtyRowIds(new Set());
        await queryClient.invalidateQueries('fullInventoryTask');
        await queryClient.invalidateQueries('fullInventoryReport');
        await queryClient.invalidateQueries('upcomingTasks');
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to save full inventory count');
      },
    }
  );

  const countedSummary = useMemo(() => ({
    counted: rows.filter((row) => row.counted).length,
    uncounted: rows.filter((row) => !row.counted).length,
  }), [rows]);

  const updateRow = (rowId, field, rawValue) => {
    const normalizedValue = String(rawValue ?? '').replace(/[^0-9]/g, '');
    const nextValue = normalizedValue === '' ? '' : Math.max(0, Number(normalizedValue) || 0);
    setRows((currentRows) => {
      const nextRows = currentRows.map((row) => {
      if (row.id !== rowId) return row;
      const nextRow = {
        ...row,
        [field]: nextValue,
      };
      const shopCount = Number(nextRow.shopCount || 0);
      const warehouseCount = Number(nextRow.warehouseCount || 0);
      return {
        ...nextRow,
        totalQuantity: shopCount + warehouseCount,
        counted: true,
      };
      });
      writeDraftRows(draftStorageKey, nextRows);
      return nextRows;
    });
    setDirtyRowIds((current) => {
      const next = new Set(current);
      next.add(String(rowId));
      return next;
    });
  };

  const buildCountPayload = (sourceRows = rows) => sourceRows.filter((row) => row.counted).map((row) => ({
      productId: row.productId,
      productName: row.productName,
      sku: row.sku,
      shopCount: Number(row.shopCount || 0),
      warehouseCount: Number(row.warehouseCount || 0),
    }));
  const handleSave = () => {
    const changedRows = rows.filter((row) => dirtyRowIds.has(String(row.id)));
    const countedRows = buildCountPayload(changedRows);

    if (!countedRows.length) {
      toast.error('Update at least one row before saving.');
      return;
    }

    saveMutation.mutate({
      date: selectedDate,
      rows: countedRows,
    });
  };

  const handleCompleteInventoryCount = () => {
    if (!rows.length) {
      toast.error('No inventory rows available to complete.');
      return;
    }
    saveMutation.mutate({
      date: selectedDate,
      completeAll: true,
      rows: [],
    });
  };

  const exportVisibleRows = () => {
    if (!filteredRows.length) return;

    downloadExcelWorkbook({
      fileName: `full-inventory-count-${selectedDate}.xlsx`,
      sheetName: 'Inventory Count',
      columns: [
        { header: 'Store', key: 'Store', width: 20 },
        { header: 'Seller', key: 'Seller', width: 20 },
        { header: 'SKU', key: 'SKU', width: 18 },
        { header: 'Barcode', key: 'Barcode', width: 20 },
        { header: 'Product', key: 'Product', width: 30 },
        { header: 'Shop Count', key: 'Shop Count', width: 12 },
        { header: 'Warehouse Count', key: 'Warehouse Count', width: 16 },
        { header: 'Total Qty', key: 'Total Qty', width: 12 },
        { header: 'Category', key: 'Category', width: 18 },
        { header: 'Parent Category', key: 'Parent Category', width: 22 },
        { header: 'Count Status', key: 'Count Status', width: 14 },
      ],
      rows: filteredRows.map((row) => ({
        Store: row.storeName,
        Seller: row.sellerName,
        SKU: row.sku,
        Barcode: row.barcode,
        Product: row.productName,
        'Shop Count': row.shopCount,
        'Warehouse Count': row.warehouseCount,
        'Total Qty': row.totalQuantity,
        Category: row.categoryName,
        'Parent Category': row.parentCategoryName,
        'Count Status': row.counted ? 'Counted' : 'Uncounted',
      })),
    });
  };

  if (!canViewPage) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Inventory Count</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Inventory Count</h1>
        <p className="mt-1 text-sm text-gray-600">
          Count all products during the 7-day full inventory window for {meta.storeName || selectedStoreName || 'your store'}.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-4">
        <div className="card border border-primary-200 bg-primary-50">
          <div className="flex items-center gap-3">
            <Store className="h-6 w-6 text-primary-600" />
            <div>
              <p className="text-sm font-medium text-primary-800">Store</p>
              <p className="text-lg font-bold text-primary-700">{meta.storeName || selectedStoreName || 'Selected Store'}</p>
            </div>
          </div>
        </div>
        <div className="card border border-emerald-200 bg-emerald-50">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            <div>
              <p className="text-sm font-medium text-emerald-800">Counted Products</p>
              <p className="text-2xl font-bold text-emerald-700">{countedSummary.counted}</p>
            </div>
          </div>
        </div>
        <div className="card border border-amber-200 bg-amber-50">
          <div className="flex items-center gap-3">
            <ShieldAlert className="h-6 w-6 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-800">Uncounted Products</p>
              <p className="text-2xl font-bold text-amber-700">{countedSummary.uncounted}</p>
            </div>
          </div>
        </div>
        <div className="card border border-slate-200 bg-slate-50">
          <div className="flex items-center gap-3">
            <CalendarDays className="h-6 w-6 text-slate-600" />
            <div>
              <p className="text-sm font-medium text-slate-700">Scheduled Date</p>
              <p className="text-lg font-bold text-slate-900">{meta.date || selectedDate}</p>
            </div>
          </div>
        </div>
      </div>

      <div className={`card border ${
        isCheckingSchedule
          ? 'border-slate-200 bg-slate-50/80'
          : isError
            ? 'border-rose-200 bg-rose-50/70'
            : isScheduled
              ? 'border-emerald-200 bg-emerald-50/60'
              : 'border-amber-200 bg-amber-50/70'
      }`}>
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">
              {isCheckingSchedule
                ? 'Checking full inventory schedule for this date...'
                : isError
                  ? 'Unable to load full inventory schedule.'
                : isScheduled
                  ? 'Full inventory count is scheduled for this date.'
                  : 'No full inventory schedule found for this date.'}
            </p>
            <p className="text-sm text-gray-600">
              {isCheckingSchedule
                ? 'Loading the latest scheduled count details for your store.'
                : isError
                  ? taskLoadErrorMessage
                : isScheduled
                  ? (schedule?.note || 'You can update product counts during this 7-day window and save anytime.')
                  : 'Ask admin to schedule a full inventory count date before counting all products.'}
            </p>
            {isTaskCompleted ? (
              <p className="mt-1 text-sm font-semibold text-emerald-700">
                Full inventory count is completed{completedAt ? ` on ${String(completedAt).slice(0, 10)}` : ''}{completedBy ? ` by ${completedBy}` : ''}.
              </p>
            ) : null}
          </div>
          {schedule ? (
            <div className="rounded-xl bg-white/80 px-3 py-2 text-xs text-gray-600 shadow-sm">
              Schedule: {schedule?.title || schedule?.label || schedule?.scheduled_date}
            </div>
          ) : null}
        </div>
      </div>

      <div className="card">
        <div className="flex flex-wrap gap-4">
          <div className="min-w-52">
            <label className="mb-1 block text-sm font-medium text-gray-700">Count Date</label>
            <input
              type="date"
              value={selectedDate}
              max={todayKey}
              onChange={(event) => setSelectedDate(event.target.value)}
              className="input"
            />
          </div>

          <FilterSelect
            label="Status"
            value={countStatus}
            options={COUNT_STATUS_OPTIONS}
            onChange={setCountStatus}
            className="min-w-52"
          />

          <div className="min-w-[280px] flex-1">
            <label className="mb-1 block text-sm font-medium text-gray-700">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search product, SKU, barcode, category, seller..."
                className="input pl-10"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Full Inventory Products</h2>
            <p className="text-sm text-gray-500">Counted and uncounted filters help you spot any products left behind.</p>
          </div>
          <div className="flex items-center gap-3">
            <ExportButton onClick={exportVisibleRows} disabled={!rows.length} />
            <button
              type="button"
              onClick={handleSave}
              disabled={!isScheduled || saveMutation.isLoading || dirtyRowIds.size === 0}
              className="btn-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saveMutation.isLoading ? 'Saving...' : 'Save Counts'}
            </button>
            <button
              type="button"
              onClick={handleCompleteInventoryCount}
              disabled={!isScheduled || saveMutation.isLoading || !rows.length}
              className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Complete Inventory Count
            </button>
          </div>
        </div>

        <div className="table-scroll-shell">
          <table className="min-w-[1460px] divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Store</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Seller</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">SKU</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 w-[20ch] min-w-[20ch] max-w-[20ch]">Barcode</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Product Name</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Shop Count</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Warehouse Count</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Total Qty</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Category</th>
                <th className="sticky top-0 bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Parent Category</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center text-gray-500">Loading full inventory products...</td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center text-rose-600">{taskLoadErrorMessage}</td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center text-gray-500">No products found for the selected date and filters.</td>
                </tr>
              ) : filteredRows.map((row) => (
                <tr key={row.id} className={row.counted ? 'bg-emerald-50/30' : 'hover:bg-gray-50'}>
                  <td className="px-4 py-3 text-sm text-gray-800">{row.storeName}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.sellerName}</td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-600">{row.sku}</td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-600 w-[20ch] min-w-[20ch] max-w-[20ch]">
                    <div
                      className="barcode-scroll-cell"
                      title={row.barcode || '-'}
                    >
                      {row.barcode || '-'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.productName}</td>
                  <td className="px-4 py-3 text-right">
                    <input
                      type="text"
                      inputMode="numeric"
                      className="input ml-auto w-24 text-right"
                      value={row.shopCount}
                      onChange={(event) => updateRow(row.id, 'shopCount', event.target.value)}
                      disabled={!isScheduled}
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <input
                      type="text"
                      inputMode="numeric"
                      className="input ml-auto w-24 text-right"
                      value={row.warehouseCount}
                      onChange={(event) => updateRow(row.id, 'warehouseCount', event.target.value)}
                      disabled={!isScheduled}
                    />
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">{row.totalQuantity}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.categoryName}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.parentCategoryName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FullInventoryCount;
